import React, { useRef, useMemo } from 'react';
import clsx from 'clsx';
import { Swiper, SwiperSlide, SwiperRef } from 'swiper/react';
import {} from 'swiper/react';
import { Navigation } from 'swiper';
import Product from '../../Types/Product';
import { LinkModel } from '../../Types/LinkModel';
import Icon from '../Icon';
import { uniqueId } from 'lodash-es';
import useReady from '../../Hooks/useReady';

export interface CustomResponsiveSliderProps {
  items: Product[] | any[];
  numPerRow: number;
  showSlider: boolean;
  preTitle?: string;
  title?: string;
  link?: LinkModel;
  mobileLayout?: 'small' | 'large';
  swiperSlideClassName?: string;
  itemRenderer: (
    item: any,
    key: any,
    className?: string,
    index?: number
  ) => Element | React.FC | any;
}

const navigationBtnClasses =
  'hidden h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700 disabled:hover:bg-gray-100 hover:text-gray-900 hover:bg-gray-200 transition-all disabled:opacity-50 [&_svg]:hover:scale-110 disabled:[&_svg]:hover:scale-100 md:flex';

const CustomResponsiveSlider = (props: CustomResponsiveSliderProps) => {
  const {
    preTitle,
    title,
    link,
    items,
    numPerRow,
    showSlider,
    mobileLayout = 'small',
    swiperSlideClassName,
    itemRenderer,
  } = props;
  // Used to render product cards without slider first, to prevent CLS
  const ready = useReady();
  const swiperRef = useRef<SwiperRef | null>(null);
  const prevBtnRef = useRef<HTMLButtonElement | null>(null);
  const nextBtnRef = useRef<HTMLButtonElement | null>(null);
  const uuid = useMemo(() => uniqueId(), []);

  const cells = {
    1: 'md:grid-cols-1',
    2: 'md:grid-cols-2',
    3: 'md:grid-cols-2 lg:grid-cols-3',
    4: 'md:grid-cols-2 lg:grid-cols-4',
    5: 'md:grid-cols-3 lg:grid-cols-5',
    6: 'md:grid-cols-3 lg:grid-cols-6',
  };

  const mediumNumPerRow = useMemo(() => {
    switch (numPerRow) {
      case 4:
        return 2;
      case 5:
      case 6:
        return 3;
      case 3:
      default:
        return numPerRow;
    }
  }, [numPerRow]);

  const mobileNumPerRow = useMemo(() => {
    switch (mobileLayout) {
      case 'small':
        return 2.11;
      case 'large':
        return 1.05;
      default:
        return 2.11;
    }
  }, [mobileLayout, mediumNumPerRow]);

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <div>
          {preTitle && (
            <span className="pretitle-sm mb-3 inline-block">
              {preTitle}
            </span>
          )}
          {title && <h2 className="font-normal">{title}</h2>}
        </div>
        <div className="flex items-center self-end">
          {link?.href && (
            <a
              href={link.href}
              className="group relative block overflow-hidden whitespace-nowrap pb-[1px] text-md leading-tight text-gray-700 store-screen:border-gray-900"
            >
              {link.text}
              <span className="absolute bottom-0 left-0 h-[1px] w-full bg-gray-700 transition-[left] duration-700 group-hover:left-[200%] store-screen:hidden"></span>
              <span className="absolute -left-[200%] bottom-0 h-[1px] w-full bg-gray-700 transition-[left] duration-700 group-hover:left-0 store-screen:hidden"></span>
            </a>
          )}
          {showSlider && items.length > numPerRow && (
            <>
              <button
                className={clsx(
                  link?.href && 'ml-4',
                  navigationBtnClasses
                )}
                ref={prevBtnRef}
                disabled={!swiperRef?.current}
              >
                <Icon name="chevron-left" className="h-5 w-5" />
              </button>
              <button
                className={clsx('ml-4', navigationBtnClasses)}
                ref={nextBtnRef}
                disabled={!swiperRef?.current}
              >
                <Icon name="chevron-right" className="h-5 w-5" />
              </button>
            </>
          )}
        </div>
      </div>
      {showSlider && ready ? (
        <Swiper
          spaceBetween={16}
          ref={swiperRef}
          slidesPerView={numPerRow}
          slidesPerGroup={numPerRow}
          modules={[Navigation]}
          navigation={{
            prevEl: prevBtnRef?.current,
            nextEl: nextBtnRef?.current,
          }}
          threshold={5}
          breakpoints={{
            0: {
              slidesPerView: mobileNumPerRow,
              slidesPerGroup: Math.floor(mobileNumPerRow),
            },
            768: {
              slidesPerView: mediumNumPerRow,
              slidesPerGroup: mediumNumPerRow,
            },
            1024: { slidesPerView: numPerRow, slidesPerGroup: numPerRow },
          }}
          className="sm-down:!-mx-4 sm-down:!px-4"
        >
          {items.map((item, idx) => (
            <SwiperSlide
              key={uuid + 'slide' + idx}
              className={clsx(
                '!h-auto',
                mobileLayout == 'small' && 'md:max-h-none',
                swiperSlideClassName
              )}
              virtualIndex={idx}
            >
              {itemRenderer(
                item,
                uuid + '-slide-' + idx,
                clsx(mobileLayout == 'small' && 'md:max-h-none'),
                idx
              )}
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <div
          className={clsx(
            'grid gap-x-4 gap-y-6 lg:gap-y-4',
            cells[numPerRow],
            showSlider &&
              (mobileLayout == 'small'
                ? 'sm-down:grid-cols-2'
                : 'sm-down:grid-cols-1'),
            !showSlider &&
              mobileLayout == 'large' &&
              'grid-cols-2 sm-down:[&>*:nth-child(5n+1)]:col-span-2',
            !showSlider && mobileLayout == 'small' && 'grid-cols-2'
          )}
        >
          {showSlider &&
            items
              .slice(0, numPerRow)
              .map((item, idx) =>
                itemRenderer(
                  item,
                  uuid + '-noslide-' + idx,
                  clsx(
                    'h-auto',
                    mobileLayout == 'small' && 'md:max-h-none',
                    numPerRow == 6 && idx > 2 && 'md-only:hidden',
                    numPerRow == 5 && idx > 2 && 'md-only:hidden',
                    numPerRow == 4 && idx > 1 && 'md-only:hidden',
                    numPerRow == 3 && idx > 1 && 'md-only:hidden',
                    numPerRow == 2 && idx > 1 && 'md-only:hidden',
                    numPerRow == 1 && idx > 0 && 'md-only:hidden',
                    mobileLayout == 'small' && idx > 1 && 'sm-down:hidden',
                    mobileLayout == 'large' && idx > 0 && 'sm-down:hidden',
                    mobileLayout == 'large' &&
                      numPerRow < 2 &&
                      idx == 0 &&
                      items.length > 1 &&
                      'md-down:mr-[5%]',
                    mobileLayout == 'small' &&
                      numPerRow < 2 &&
                      idx == 0 &&
                      items.length > 2 &&
                      'md-down:mr-[11%]'
                  ),
                  idx
                )
              )}
          {!showSlider &&
            items.map((item, idx) =>
              itemRenderer(item, uuid + '-noslide-' + idx, '', idx)
            )}
        </div>
      )}
    </>
  );
};

export default CustomResponsiveSlider;
